/**
 * 對話訊息嘅渲染器（UI v2 步 5：由 `ChatPage.tsx` **純搬**出嚟，零行為改動）
 *
 *   · `renderExtra(m)`       → 訊息下方插入物（歌訊息＝內嵌播放器／歌詞編輯中＝逐句編輯器）
 *   · `messageActions(m)`    → 訊息右下角動作掣（`decisions/025`／`026`）
 *
 * ⚠️ 呢兩個函數**閉包住好多 ChatPage 狀態／處理器** → 用一個 `ctx` 傳入（唔改任何邏輯）。
 */
import type { Dispatch, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import type { ChatMessage } from '@coeldery/chat-thread';
import AudioPlayer from '@coeldery/audio-player';
import LyricsEditor from '@coeldery/lyrics-editor';
import ShareButton from '@coeldery/share-button';
import { canPlaySong } from '../../player/playerModel';
import type { useLibrary } from '../../hooks/useLibrary';
import type { usePlayer } from '../../player/PlayerContext';
import type { VocalGender } from './types';
import { ROW_BTN, PRIMARY_BTN } from './styles';

export interface MessageRendererCtx {
  t: TFunction;
  navigate: (to: string) => void;
  songs: ReturnType<typeof useLibrary>['songs'];
  player: ReturnType<typeof usePlayer>;
  playSong: (songId: string) => void;
  editSongId: string | null;
  editPicked: number[];
  editDrafts: Record<number, string>;
  busyAction: boolean;
  toggleEditLine: (i: number) => void;
  setDraft: (i: number, v: string) => void;
  lyricsOf: (songId: string) => string;
  renderVariants: 1 | 2;
  setRenderVariants: Dispatch<SetStateAction<1 | 2>>;
  vocalGender: VocalGender;
  setVocalGender: Dispatch<SetStateAction<VocalGender>>;
  voiceReady: boolean;
  useVoice: boolean;
  setUseVoice: Dispatch<SetStateAction<boolean>>;
  runRender: (songId: string) => void;
  runWriteLyrics: () => void;
  setArmed: (v: 'exact' | 'meaning' | null) => void;
  startEdit: (songId: string) => void;
  applyWithAi: (songId: string) => void;
  applyDirect: (songId: string) => void;
  exitEdit: () => void;
  renderingId: string | null;
  openStyle: (songId: string) => void;
  setPlaylistSongId: (songId: string) => void;
  shareFiles: Record<string, File>;
  audioUrlForShare: (songId: string) => string;
  langBtn: (active: boolean) => string;
}

export function createMessageRenderers(ctx: MessageRendererCtx) {
  const {
    t, navigate, songs, player, playSong,
    editSongId, editPicked, editDrafts, busyAction, toggleEditLine, setDraft, lyricsOf,
    renderVariants, setRenderVariants, vocalGender, setVocalGender, voiceReady, useVoice, setUseVoice,
    runRender, startEdit, applyWithAi, applyDirect, exitEdit, renderingId,
    runWriteLyrics, setArmed,
    openStyle, setPlaylistSongId, shareFiles, audioUrlForShare, langBtn,
  } = ctx;

  /** ⭐ 插位：歌訊息 → 內嵌播放器；歌詞訊息（編輯中）→ 逐句編輯器 */
  const renderExtra = (m: ChatMessage) => {
    if (m.kind === 'song' && m.songId) {
      const src = songs.find((s) => s.id === m.songId);
      const songId = m.songId;
      const isCurrent = player.track?.id === songId;
      return (
        <>
          <AudioPlayer
            variant="mini"
            title={src?.title || m.text}
            playing={isCurrent && player.playing}
            positionMs={isCurrent ? player.positionMs : 0}
            durationMs={isCurrent ? player.durationMs : null}
            error={isCurrent ? player.error : null}
            onToggle={() => (isCurrent ? player.toggle() : playSong(songId))}
            onOpen={() => navigate('/player')}
          />
          {/* ⭐ 2026-09-23：**未有音頻就要照實講**（唔准靜靜；之前 ▶ 撳落去冇反應） */}
          {!canPlaySong(src) ? (
            <p className="footnote" data-testid="song-audio-pending" style={{ margin: '2px 0 0', color: 'var(--color-text-secondary)' }}>
              {t('song_audio_pending')}
            </p>
          ) : null}
        </>
      );
    }
    if (m.kind === 'lyrics' && m.songId && editSongId === m.songId) {
      const songId = m.songId;
      return (
        <LyricsEditor
          lyrics={lyricsOf(songId)}
          picked={editPicked}
          drafts={editDrafts}
          busy={busyAction}
          onToggleLine={toggleEditLine}
          onDraftChange={setDraft}
          hintKey="edit_hint"
        />
      );
    }
    return null;
  };

  /** ⭐ 訊息右下角動作（`decisions/025`／`026`） */
  const messageActions = (m: ChatMessage) => {
    if (m.kind === 'understanding') {
      return (
        <>
          <button
            type="button"
            data-testid="act-write"
            disabled={busyAction}
            onClick={() => void runWriteLyrics()}
            style={{ ...ROW_BTN, ...PRIMARY_BTN, minHeight: '36px' }}
          >
            {busyAction ? t('song_generating') : t('understanding_ok')}
          </button>
          <button
            type="button"
            data-testid="act-refine"
            disabled={busyAction}
            onClick={() => setArmed(null)}
            style={{ ...ROW_BTN, minHeight: '36px', borderColor: 'var(--color-divider)' }}
          >
            {t('understanding_fix')}
          </button>
        </>
      );
    }
    if (m.kind === 'lyrics' && m.songId) {
      const songId = m.songId;
      if (editSongId === songId) {
        return (
          <>
            <button
              type="button"
              data-testid="act-apply-ai"
              disabled={busyAction}
              onClick={() => void applyWithAi(songId)}
              style={{ ...ROW_BTN, ...PRIMARY_BTN, minHeight: '36px' }}
            >
              {busyAction ? t('song_generating') : t('edit_apply_ai')}
            </button>
            <button
              type="button"
              data-testid="act-apply-direct"
              disabled={busyAction}
              onClick={() => void applyDirect(songId)}
              style={{ ...ROW_BTN, minHeight: '36px', borderColor: 'var(--color-divider)' }}
            >
              {t('edit_apply_direct')}
            </button>
            <button
              type="button"
              data-testid="act-edit-done"
              disabled={busyAction}
              onClick={exitEdit}
              style={{ ...ROW_BTN, minHeight: '36px', borderColor: 'var(--color-divider)' }}
            >
              {t('edit_done')}
            </button>
          </>
        );
      }
      return (
        <>
          {/* ⭐ **2026-09-26（`decisions/051`）**：**男聲／女聲／自動** */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }} data-testid="vocal-gender">
            {(
              [
                ['auto', 'gender_auto'],
                ['male', 'gender_male'],
                ['female', 'gender_female'],
              ] as const
            ).map(([id, key]) => (
              <button
                key={id}
                type="button"
                data-testid={`gender-${id}`}
                aria-pressed={vocalGender === id}
                className={langBtn(vocalGender === id)}
                disabled={busyAction}
                onClick={() => setVocalGender(id)}
              >
                {t(key)}
              </button>
            ))}
          </div>
          {/* ⭐ 2026-09-23：〔用我把聲唱〕可以逐首開／關；2026-09-26：只有真係配置咗聲線才顯示 */}
          {voiceReady ? (
            <button
              type="button"
              data-testid="voice-toggle"
              aria-pressed={useVoice}
              disabled={busyAction}
              onClick={() => setUseVoice((v) => !v)}
              style={{ ...ROW_BTN, minHeight: '36px', borderColor: 'var(--color-divider)' }}

            >
              {useVoice ? t('voice_mine_on') : t('voice_mine_off')}
            </button>
          ) : null}
          {/* ⭐ 0017：**出幾個版本由用戶揀**（1 個／2 個） */}
          <button
            type="button"
            data-testid="variants-toggle"
            aria-pressed={renderVariants === 2}
            disabled={busyAction}
            onClick={() => setRenderVariants((v) => (v === 2 ? 1 : 2))}
            style={{ ...ROW_BTN, minHeight: '36px', borderColor: 'var(--color-divider)' }}
          >
            {renderVariants === 2 ? t('render_variants_two') : t('render_variants_one')}
          </button>
          <button
            type="button"
            data-testid="act-render"
            disabled={busyAction || renderingId === songId}
            onClick={() => void runRender(songId)}
            style={{ ...ROW_BTN, ...PRIMARY_BTN, minHeight: '36px' }}
          >
            {busyAction || renderingId === songId ? t('song_generating') : t('song_generate_button')}
          </button>
          <button
            type="button"
            data-testid="act-edit"
            disabled={busyAction}
            onClick={() => startEdit(songId)}
            style={{ ...ROW_BTN, minHeight: '36px', borderColor: 'var(--color-divider)' }}
          >
            {t('lyrics_sheet_edit')}
          </button>
          <button
            type="button"
            onClick={() => void navigator.clipboard?.writeText(m.text)}
            style={{ ...ROW_BTN, minHeight: '36px', borderColor: 'var(--color-divider)' }}
          >
            {t('lyrics_copy')}
          </button>
        </>
      );
    }
    if (m.kind === 'song' && m.songId) {
      const songId = m.songId;
      const src = songs.find((s) => s.id === songId);
      return (
        <>
          <ShareButton
            title={src?.title || m.text}
            text={t('share_song_text')}
            url="https://cosingery.com"
            /* 分享首歌本身 —— mp3（唔用 flac：幾十 MB 唔適合分享）＋檔名＝歌名 */
            fileUrl={audioUrlForShare(songId)}
            /* 已背景準備好嘅檔案 → 撳掣即刻分享（保住用戶手勢） */
            prefetchedFile={shareFiles[songId] ?? null}
            filename={src?.title ? `${src.title}.mp3` : undefined}
            style={{ ...ROW_BTN, minHeight: '36px', borderColor: 'var(--color-divider)' }}
          />
          {/* ⭐ 0013（`decisions/033`）：〔分享〕隔離嘅〔加入播放清單〕（產品負責人指定） */}
          <button
            type="button"
            data-testid="act-add-playlist"
            onClick={() => setPlaylistSongId(songId)}
            style={{ ...ROW_BTN, minHeight: '36px', borderColor: 'var(--color-divider)' }}
          >
            {t('playlist_add_button')}
          </button>
          <button
            type="button"
            data-testid="act-remake"
            disabled={busyAction}
            onClick={() => openStyle(songId)}
            style={{ ...ROW_BTN, minHeight: '36px', borderColor: 'var(--color-divider)' }}
          >
            {t('remake_button')}
          </button>
          <button
            type="button"
            data-testid="act-go-player"
            onClick={() => navigate('/player')}
            style={{ ...ROW_BTN, minHeight: '36px', borderColor: 'var(--color-divider)' }}
          >
            {t('go_player')}
          </button>
        </>
      );
    }
    return null;
  };

  return { renderExtra, messageActions };
}
